import React from "react";
import ConfirmPopup from "components/ConfirmPopup";
import Vocabulary from "components/Vocabulary";
import ProductAPI from "restAPI/ProductAPI";
import ModalUtil from "utils/modalUtil";
import MoveBusinessUnitFormPopup from "./MoveBusinessUnitFormPopup";
import DeletePopup from "components/DeletePopup";
import htmlToMarkDown, { sanitizeMarkdown } from "utils/htmlToMarkDownUtil";
import { appendVersionToName, getDefaultTierValues, NewVersionPopup, removeVersionFromName } from "../common";
import _, { isEmpty, isFunction } from "lodash";
import { marked } from "marked";
import { nameRegexValidator } from "utils/formUtils";
import { CUSTOM_TIER } from "utils/tierUtil";
import message from "components/CustomMessage";
import { hasPermission } from "utils/authService";
import { PRODUCT_ACTION_PERMISSION_MAP } from "constants/RBACConstants";
import CVSSModifierPopup from "components/cvss/CVSSModifierPopup";

export const openProductArchivePopup = (activeProductId, history, refresh) => {
  ModalUtil.show({
    content: (
      <ConfirmPopup
        promptMessage={
          <>
            This <Vocabulary name={"product"} /> and all it's{" "}
            <Vocabulary name={"subProduct"} /> will be moved into the archive
            Organization, and all security tool configurations will be disabled
          </>
        }
        okText="Archive"
        note
        onOk={async (note) => {
          await ProductAPI.archiveProduct(activeProductId, note).promise.then(() => {
            message.success(
              <>
                <Vocabulary name={"product"} /> successfully archived.
              </>
            );
            if(isFunction(refresh)){
              refresh();
            }
            else {
              history.push("/product");
            }
          });
        }}
      />
    ),
  });
};

export const openMoveProductPopup = (activeProductId, name, afterClose) => {
  ModalUtil.show({
    content: (
      <MoveBusinessUnitFormPopup
        productId={activeProductId}
        productName={name}
      />
    ),
    afterClose: (success) => {
        if (success) {
          afterClose()
        }
      }
  });
};

export const openDeleteProductPopup = async (id, name, activeUser, refresh) => {

  const onDelete = async (note) => {
    await activeUser.isArchiveBU ? ProductAPI.delete(id) : ProductAPI.archiveDelete(id, note);
    message.success(`${name} deletion initiated.`);
    if(isFunction(refresh)){
      refresh()
    }
  };
  ModalUtil.show({
    content: (
      <DeletePopup confirmText={"Delete"} onDelete={onDelete} />
    ),
  });
};

export const openProductNewVersionPopup = ({ product, afterClose }) => {
  const defaultStatus = product.status || "";
  const initialValues = {
    existingVersion: product.versionNumber,
    cloneManualFindings: false,
    status: defaultStatus
  };
  const onSubmit = async (data) => {
    const response = await ProductAPI.createVersion({ productId: +product.id, version: data.version, status: data.status, cloneManualFindings: data.cloneManualFindings }).promise;
    return response;
  };
  ModalUtil.show({
    content: (
      <NewVersionPopup initialValues={initialValues} onSubmit={onSubmit} />
    ),
    afterClose: (newProduct) => {
      if (newProduct) {
        message.success('Request is accepted. Please check after sometime.');
      }
      afterClose(newProduct);
    }
  });
};

export const ValueToIdList = ["type", "engineeringOwner", "securityOwner", "businessOwner", "complianceOwner", "supportOwner", "teamDto"];

export const updateDataBeforePush = (
  data,
  confidentialityData,
  tierCategory
) => {
  // Append version name in name
  data.name = appendVersionToName(data);

  if(data.tier === CUSTOM_TIER){
    data.tier = null;
  }

  delete data.confidentiality_custom;
  ValueToIdList.forEach((v) => {
    if (data[v]) {
      data[v] = {
        id: data[v],
      };
    }
  });

  // convert html to markdown
  if (data.description) {
    data.description = htmlToMarkDown(data.description);
  }
  data.slaTemplateId = data.slaTemplateId || null;
  let keys = Object.keys(confidentialityData);
  if (data.tier && isEmpty(data.confidentialityOptions) && !keys.length) {
    const { selectedTierGlobalFields, categorizationConfidentiality } =
      getDefaultTierValues(data.tier, tierCategory);
    
    // In case of skip and save, the assessment settings for the selected tier isn't received by the form submit. Hence we add them manually
    for(const i in selectedTierGlobalFields){
      if(!_.has(data, i)){
        data[i] = selectedTierGlobalFields[i];
      }
    }
    keys = Object.keys(categorizationConfidentiality);
  }
  const length = keys.length;
  data.confidentiality =
    length === 3 ? "ALL" : length === 2 ? "TWO" : length === 1 ? "ONE" : "NONE";
  data.confidentialityOptions = keys.join(",");

  return data;
};

export const updateDataAfterGet = (data) => {
  ValueToIdList.forEach((v) => {
    if (data[v]) {
      data[v] = data[v].id;
    }
  });
  // Remove version name from name
  data.name = removeVersionFromName(data);

  if(!data.tier){
    data.tier = CUSTOM_TIER;
  } else {
    data.tier = !_.isNaN(+data.tier) ? +data.tier : data.tier;
  }

  // convert markdown to html
  if (data.description) {
    data.description = marked.parse(sanitizeMarkdown(data.description));
  }
  return data;
};


export const productNameFieldRules = (existingProductName, validateAPIRef, form, isVersionsEnabled = false) => {
  return [
    {
      required: true,
      message: "Enter name"
    },
    nameRegexValidator({ regex: "", max: 100 }),
    {
      validator: (_, value) => {
        if (typeof value === "string" && value?.trim().length !== 0) {
          // const remains = value
          //   .split(NAME_REGEX_START_WITH_NUMBER)
          //   .filter(Boolean).length;
          const versionNumber = form.getFieldValue("versionNumber");
          value = appendVersionToName({ name: value, versionNumber });
          if (
            existingProductName?.toLowerCase() !== value.toLowerCase() &&
            value.trim().length <= (versionNumber ? 228 : 100)
          ) {
            // cancel the api
            if (validateAPIRef.current) {
              validateAPIRef.current();
            }

            const { promise, cancel } = ProductAPI.validate({ name: value });

            validateAPIRef.current = cancel;

            const showVersionMessage = isVersionsEnabled && versionNumber;
            return promise.catch((res) =>
              Promise.reject(`Name ${showVersionMessage ? '& version combination' : ''} already exists.`)
            );
          }
        }
        return Promise.resolve();
      },
    },
  ];
};

export const ProductModalCommonConfig = {
  width: 778,
  mask: true,
  settingsPrefixKey: "product-details",
  defaultPlacement: "right",
  resize: true,
};

export const updateBasicDataAfterGet = (data) => {
   // Convert type from { id } to primitive value
  if (data.type) {
    data.type = data.type.id;
  }

  // Remove version name from name
  data.name = removeVersionFromName(data);

   // If tier is null, assign CUSTOM_TIER
  if(!data.tier){
    data.tier = CUSTOM_TIER;
  } else {
    data.tier = !_.isNaN(+data.tier) ? +data.tier : data.tier;
  }

  // convert markdown to html
  if (data.description) {
    data.description = marked.parse(data.description);
  }
  return data;
};

export const updateBasicDataBeforePush = (data) => {
  
  // Append version name in name
  data.name = appendVersionToName(data);

  // Convert CUSTOM_TIER to null
  if(data.tier === CUSTOM_TIER){
    data.tier = null;
  }

  // Convert `type` to { id: type }
  if (data.type) {
    data.type = { id: data.type };
  }

  // Convert description from HTML to Markdown (if present)
  if (data.description) {
    data.description = htmlToMarkDown(data.description);
  }

  return data;
};

export const getDisabledActionAndTooltip = (action) => {
  const noPermission = !hasPermission(
    PRODUCT_ACTION_PERMISSION_MAP[action],
  );
  return {
    tooltip: noPermission ? "No Access" : "",
    disabled: noPermission,
  };
};

export const openNewVersionPopup = (record, refresh) => {
  if (isEmpty(record)) {
    return;
  }
  const afterClose = (newProduct) => {
    if (newProduct) {
      refresh();
    }
  };
  openProductNewVersionPopup({ product: record, afterClose });
};

export const openCVSSModifierModal = (productId, modalRenderer, initialCVSSVector, refresh) => {
  const onSubmit = async (cvssVector) => {
    await ProductAPI.updateOtherProperties(productId, {
      properties: {
        modifiedVector: cvssVector,
      },
    }).promise;
  };
  modalRenderer.show({
    content: (
      <CVSSModifierPopup
        onSubmit={onSubmit}
        titlePrefix={<Vocabulary name="product" />}
        subtitle={""}
        showHeader={false}
        initialCvSSString={initialCVSSVector}
      />
    ),
    width: 1056,
    onClose: refresh
  });
};
